import { createContext, useContext, useMemo } from "react";
import { CursorType } from "frontend/canvas-designer-new/cursor-type";
import type { PointInViewport } from "frontend/utils/math-utils";
import * as MathUtils from "frontend/utils/math-utils";

export const FooterContext = createContext<{
  onFocusElement: (id: string) => void;
  onFrameDeleted: (id: string) => void;
  onChangePosScale(posScale: { scale: number; x: number; y: number }): void;
  onOrgChartTipClick(): void;
  onCursorTypeChange: (type: CursorType) => void;
  onChooseIntegrationItems: (items: any[], pos?: PointInViewport) => void;
  didDropMondayElement?: (itemData: any, position: MathUtils.PointInViewport) => void;
}>({
  onFocusElement: () => {},
  onFrameDeleted: () => {},
  onChangePosScale: () => {},
  onOrgChartTipClick: () => {},
  onCursorTypeChange: () => {},
  onChooseIntegrationItems: () => {},
  didDropMondayElement: undefined,
});

export const FooterProvider = ({
  children,
  onFocusElement,
  onFrameDeleted,
  onChangePosScale,
  onOrgChartTipClick,
  onCursorTypeChange,
  onChooseIntegrationItems,
  didDropMondayElement,
}: {
  children: React.ReactNode;
  onFocusElement: (id: string) => void;
  onFrameDeleted: (id: string) => void;
  onChangePosScale: (posScale: { scale: number; x: number; y: number }) => void;
  onOrgChartTipClick: () => void;
  onCursorTypeChange: (type: CursorType) => void;
  onChooseIntegrationItems: (items: any[], pos?: PointInViewport) => void;
  didDropMondayElement?: (itemData: any, position: MathUtils.PointInViewport) => void;
}) => {
  const value = useMemo(
    () => ({
      onFocusElement,
      onFrameDeleted,
      onChangePosScale,
      onOrgChartTipClick,
      onCursorTypeChange,
      onChooseIntegrationItems,
      didDropMondayElement,
    }),
    [
      onFocusElement,
      onFrameDeleted,
      onChangePosScale,
      onOrgChartTipClick,
      onCursorTypeChange,
      onChooseIntegrationItems,
      didDropMondayElement,
    ]
  );
  return <FooterContext.Provider value={value}>{children}</FooterContext.Provider>;
};

export const useFooterContext = () => {
  const context = useContext(FooterContext);
  if (!context) {
    throw new Error("useFooterContext must be used within an FooterProvider");
  }
  return context;
};

export const useSetDidDropMondayElement = (
  didDropMondayElement: (itemData: any, position: MathUtils.PointInViewport) => void
) => {
  const footerContext = useFooterContext();
  footerContext.didDropMondayElement = didDropMondayElement;
};
